import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit, Trash2, Save, XCircle, AlertCircle, CheckCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../utils/supabase';

const BenefitsManagement = () => {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [form, setForm] = useState({
    title: '', 
    description: '', 
    image_url: '', 
    is_active: true
  });
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const RECOMMENDED_DIMENSIONS = "600x400px (relación de aspecto 3:2)";

  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('benefits')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching benefits:', error);
        setError('Error al cargar los beneficios: ' + error.message);
        setBenefits([]);
      } else {
        setBenefits(data || []);
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Error de conexión. Verifica tu internet.');
      setBenefits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setMessage(null);

    try {
      const bucketName = 'images-club-mia';
      const filePath = `public/benefits/${Date.now()}_${file.name}`;

      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw new Error(`Error al subir imagen: ${error.message}`);
      }

      const publicURL = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath).data.publicUrl;

      console.log('🔗 URL pública generada para beneficio:', publicURL);

      // Actualizar el formulario con la nueva URL
      setForm({ ...form, image_url: publicURL });

      // Si estamos editando un beneficio existente, actualizar la DB inmediatamente
      if (editingBenefit) {
        console.log('🔄 Actualizando URL de imagen en la DB para beneficio existente...');
        try {
          const { data: updateData, error: updateError } = await supabase
            .from('benefits')
            .update({ image_url: publicURL })
            .eq('id', editingBenefit.id)
            .select();

          if (updateError) {
            console.error('❌ Error actualizando imagen en DB:', updateError);
            setMessage({ type: 'warning', text: 'Imagen subida, pero error al actualizar en la base de datos: ' + updateError.message });
          } else {
            console.log('✅ URL de imagen actualizada en DB para beneficio:', updateData[0]);
            // Actualizar el item en la lista
            setBenefits(benefits.map((benefit) => (benefit.id === editingBenefit.id ? updateData[0] : benefit)));
            setMessage({ type: 'success', text: 'Imagen subida y actualizada en la base de datos automáticamente.' });
          }
        } catch (dbError) {
          console.error('💥 Error inesperado actualizando DB:', dbError);
          setMessage({ type: 'warning', text: 'Imagen subida, pero error inesperado al actualizar la base de datos.' });
        }
      } else {
        setMessage({ type: 'success', text: 'Imagen subida con éxito. Recuerda guardar el beneficio para completar la creación.' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (benefit) => {
    setEditingBenefit(benefit);
    setForm({
      title: benefit.title,
      description: benefit.description || '',
      image_url: benefit.image_url || '',
      is_active: benefit.is_active !== undefined ? benefit.is_active : true
    });
    setMessage(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este beneficio?')) return;

    try {
      const { error } = await supabase
        .from('benefits')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting benefit:', error);
        setMessage({ type: 'error', text: `Error al eliminar el beneficio: ${error.message}` });
      } else {
        setBenefits(benefits.filter((benefit) => benefit.id !== id));
        setMessage({ type: 'success', text: 'Beneficio eliminado con éxito.' });
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setMessage({ type: 'error', text: `Error inesperado: ${error.message}` });
    }
  };

  const handleSubmit = async (e) => {
    console.log('🚀 handleSubmit called');
    e.preventDefault();
    setMessage(null);

    console.log('📝 Form data:', form);

    // Validación
    if (!form.title.trim() || !form.description.trim()) {
      console.log('❌ Validation failed');
      setMessage({ type: 'error', text: 'El título y la descripción son obligatorios.' });
      return;
    }

    console.log('✅ Validation passed');

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      image_url: form.image_url || '',
      is_active: form.is_active !== undefined ? form.is_active : true,
    };

    console.log('📦 Payload to send:', payload);
    console.log('🔄 Starting Supabase operation...');

    try {
      if (editingBenefit) {
        console.log('✏️ Updating existing benefit...');
        const { data, error } = await supabase
          .from('benefits')
          .update(payload)
          .eq('id', editingBenefit.id)
          .select();

        console.log('📥 Update response:', { data, error });

        if (error) {
          console.error('❌ Error updating benefit:', error);
          setMessage({ type: 'error', text: `Error al actualizar el beneficio: ${error.message}` });
        } else {
          console.log('✅ Benefit updated successfully');
          setBenefits(benefits.map((benefit) => (benefit.id === editingBenefit.id ? data[0] : benefit)));
          setMessage({ type: 'success', text: 'Beneficio actualizado con éxito.' });
          setEditingBenefit(null);
          setForm({ title: '', description: '', image_url: '', is_active: true });
        }
      } else {
        console.log('➕ Creating new benefit...');

        const { data, error } = await supabase
          .from('benefits')
          .insert([payload])
          .select();

        console.log('📥 Insert response:', { data, error });

        if (error) {
          console.error('❌ Error adding benefit:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          setMessage({ type: 'error', text: `Error al añadir el beneficio: ${error.message}` });
        } else {
          console.log('✅ Benefit added successfully:', data[0]);
          setBenefits([data[0], ...benefits]);
          setMessage({ type: 'success', text: 'Beneficio añadido con éxito.' });
          setForm({ title: '', description: '', image_url: '', is_active: true });
        }
      }
    } catch (error) {
      console.error('💥 Unexpected error:', error);
      setMessage({ type: 'error', text: `Error inesperado: ${error.message}` });
    }

    console.log('🏁 handleSubmit completed');
  };

  const handleCancelEdit = () => {
    setEditingBenefit(null);
    setForm({ title: '', description: '', image_url: '', is_active: true });
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="ml-4 text-gray-600">Cargando beneficios...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="p-8 bg-gray-50 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-4xl font-bold text-gray-800">Gestión de Beneficios</h2>
        <button
          onClick={fetchBenefits}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Actualizar
        </button>
      </div>

      {error && (
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchBenefits}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Formulario de Añadir/Editar Beneficio */}
      <motion.div
        className="bg-white rounded-xl shadow-md p-6 mb-8"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h3 className="text-2xl font-bold text-gray-800 mb-4">
          {editingBenefit ? 'Editar Beneficio' : 'Añadir Nuevo Beneficio'}
        </h3>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <input
            type="text"
            name="title"
            placeholder="Título del beneficio"
            value={form.title}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
          <input
            type="url"
            name="image_url"
            placeholder="URL de la imagen"
            value={form.image_url}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <textarea
            name="description"
            placeholder="Descripción del beneficio"
            value={form.description}
            onChange={handleInputChange}
            className="md:col-span-2 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 h-32 resize-none"
            required
          />

          {/* Subida de imagen */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subir Imagen ({RECOMMENDED_DIMENSIONS})
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload"
                className={`flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading ? 'Subiendo...' : 'Seleccionar Imagen'}
              </label>
              {form.image_url && (
                <img src={form.image_url} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
              )}
            </div>
          </div>

          {/* Checkbox de Activo */}
          <div className="md:col-span-2 flex items-center">
            <input
              type="checkbox"
              name="is_active"
              id="is_active_benefit"
              checked={form.is_active}
              onChange={handleInputChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active_benefit" className="ml-2 block text-sm text-gray-900">
              Activo
            </label>
          </div>

          <div className="md:col-span-2 flex justify-end space-x-4 mt-4">
            {editingBenefit && (
              <motion.button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center px-6 py-3 bg-gray-300 text-gray-800 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <XCircle className="w-5 h-5 mr-2" /> Cancelar
              </motion.button>
            )}
            <motion.button
              type="submit"
              className="flex items-center px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Save className="w-5 h-5 mr-2" />
              {editingBenefit ? 'Actualizar' : 'Añadir'} Beneficio
            </motion.button>


          </div>
        </form>

        {/* Mensajes */}
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mt-4 p-3 rounded-xl flex items-center gap-2 ${
                message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}
            >
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Lista de Beneficios */}
      {!error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {Array.isArray(benefits) && benefits.map((benefit) => (
              <motion.div
                key={benefit.id}
                className="bg-white rounded-xl shadow-md overflow-hidden"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ y: -5, boxShadow: "0 15px 30px rgba(0,0,0,0.1)" }}
                transition={{ duration: 0.3 }}
              >
                {benefit.image_url && (
                  <img
                    src={benefit.image_url}
                    alt={benefit.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{benefit.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      benefit.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {benefit.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 line-clamp-3">{benefit.description}</p>
                  <div className="flex justify-end space-x-2">
                    <motion.button
                      onClick={() => handleEdit(benefit)}
                      className="flex items-center px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Editar
                    </motion.button>
                    <motion.button
                      onClick={() => handleDelete(benefit.id)}
                      className="flex items-center px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default BenefitsManagement;
